/**
 * Первый запуск программы: где стоит сервер заведения.
 *
 * В браузере такого экрана нет — касса открыта с того же адреса,
 * что и сервер. Программа же лежит на диске и сама по себе не знает,
 * куда слать чеки: у каждого заведения свой сервер.
 *
 * Спрашиваем один раз и запоминаем. Смена адреса — редкий случай
 * (переезд на другой сервер), и делается тем же экраном.
 */
import { useState } from 'react';
import { useKeyboard } from './useKeyboard';

export function ServerSetup({ initial, onSaved }: {
  initial?: string;
  onSaved: (base: string) => void;
}) {
  const [addr, setAddr] = useState(initial ?? 'https://');
  const [state, setState] = useState<'idle' | 'checking' | 'bad'>('idle');
  const [why, setWhy] = useState('');

  // Первый экран программы на планшете: системной клавиатуры может
  // не быть вовсе, а адрес набрать надо. Точка, дефис и косая черта —
  // в символьном ряду нашей клавиатуры
  const setAddrClean = (v: string) => { setAddr(v); setState('idle'); };
  const kb = useKeyboard(addr, setAddrClean, 'text');

  const check = async () => {
    const base = addr.trim().replace(/\/+$/, '');
    if (!/^https?:\/\/.+/.test(base)) {
      setState('bad');
      setWhy('Адрес должен начинаться с https:// — так его выдал вам поставщик');
      return;
    }

    setState('checking'); setWhy('');
    try {
      /*
       * Проверяем связь ДО сохранения.
       *
       * Кассир вводит адрес один раз и на весь срок службы кассы.
       * Ошибка в букве обнаружилась бы в час пик, когда первый чек
       * не ушёл, — и разбираться было бы некому.
       */
      const r = await fetch(`${base}/api/v1/health`, {
        signal: AbortSignal.timeout(8000),
      });
      if (!r.ok) {
        setState('bad');
        setWhy(`Сервер ответил ошибкой ${r.status}. Проверьте адрес.`);
        return;
      }
      const d = await r.json() as { ok?: boolean };
      if (!d.ok) {
        setState('bad');
        setWhy('По этому адресу отвечает не сервер Дастархана.');
        return;
      }

      try { localStorage.setItem('dastarhan.api', base); } catch { /* приват */ }
      const shell = (window as { dastarhan?: { setApiBase?: (b: string) => Promise<unknown> } }).dastarhan;
      if (shell?.setApiBase) await shell.setApiBase(base);
      onSaved(base);
    } catch (e) {
      setState('bad');
      setWhy(String(e).includes('timeout')
        ? 'Сервер не ответил за восемь секунд. Проверьте интернет и адрес.'
        : 'Не удалось связаться. Проверьте интернет и адрес.');
    }
  };

  return (
    <div className="setup">
      <h1>Куда касса будет слать чеки</h1>
      <p className="setup-hint">
        Адрес сервера вашего заведения — тот же, по которому открывается
        бэк-офис. Спрашиваем один раз, дальше касса помнит сама.
      </p>

      <label className="setup-field">
        Адрес сервера
        <input
          {...kb.props}
          autoFocus
          value={addr}
          inputMode="url"
          placeholder="https://dastarhan.duckdns.org"
          onChange={(e) => setAddrClean(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void check(); }}
        />
      </label>

      {kb.node}

      {state === 'bad' && <p className="setup-bad">{why}</p>}

      <button className="setup-go" disabled={state === 'checking'}
        onClick={() => void check()}>
        {state === 'checking' ? 'Проверяем связь…' : 'Проверить и продолжить'}
      </button>

      <p className="setup-note">
        Касса работает и без интернета: чеки копятся и уходят, когда
        связь вернётся. Но адрес нужен сейчас — иначе им некуда идти.
      </p>
    </div>
  );
}
